// =============================================
// src/components/modals/FeaturesModal.jsx
// =============================================

import React from "react";
import { Brain, Clock, Database, Dna, RefreshCw, X, FileSearch } from "lucide-react";

export default function FeaturesModal({ open, onClose }) {
  if (!open) return null;

  const features = [
    {
      icon: <Database size={24} />,
      title: "Multi-Modal Data Harmonization",
      description:
        "Uniquely integrates AlzDataLENS multi-omics, AlzGNN spatial pathology predictions, DRIAD drug perturbations, Alzforum curated literature, and our brain cell atlas.",
      color: "#0891b2",
    },
    {
      icon: <RefreshCw size={24} />,
      title: "Closed-Loop Agent",
      description:
        "Each step is conditioned on the prior output. Example: query DataLENS → re-weight with AlzGNN → cross-validate with Alzforum and DRIAD → refine rankings until convergence. The agent autonomously iterates until it reaches optimal target prioritization.",
      color: "#8b5cf6",
    },
    {
      icon: <Brain size={24} />,
      title: "Set It and Forget It Monitoring",
      description:
        "Submit your research question and relax! Our AI agent continuously watches all data sources, automatically re-runs analyses when new data appears, and alerts you to emerging therapeutic opportunities. Your research continues even while you sleep.",
      color: "#dc2626",
    },
    {
      icon: <Clock size={24} />,
      title: "Months to Hours Acceleration",
      description:
        "Transform traditional multi-month target discovery workflows into hours of automated analysis. One query replaces weeks of manual literature review, data integration, and bioinformatics.",
      color: "#ea580c",
    },
    {
      icon: <Dna size={24} />,
      title: "Disease-Specific GNN",
      description: "Proprietary Alz-GNN trained on AD single-nucleus RNA-Sequencing, spatial transcriptomics and pathology data.",
      color: "#059669",
    },
    {
      icon: <FileSearch size={24} />,
      title: "RAG-Enabled GPT for Literature Integration",
      description: "A retrieval-augmented GPT model mines Alzforum-curated PubMed IDs and clinical trial databases. It surfaces relevant evidence snippets, aligns them with omics and drug data, and generates transparent citations—ensuring reasoning is explainable and evidence-linked.",
      color: "#8a6ddbff",
    },
  ];

  return (
    <>
      <div
        style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(15,23,42,0.75)", backdropFilter: "blur(4px)", zIndex: 999 }}
        onClick={onClose}
      />
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: "white",
          borderRadius: "16px",
          padding: "28px",
          maxWidth: "900px",
          width: "90%",
          maxHeight: "85vh",
          overflow: "auto",
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
          zIndex: 1000,
          border: "1px solid #e2e8f0",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "32px", borderBottom: "2px solid #f1f5f9", paddingBottom: "20px" }}>
          <div>
            <h3 style={{ margin: "0 0 8px 0", color: "#0f172a", fontSize: "28px", fontWeight: "700" }}>
              AlzTxPILOT: Agentic AI for Alzheimer's Drug Discovery
            </h3>
            <p style={{ color: "#64748b", fontSize: "16px", margin: 0, fontStyle: "italic" }}>
              Submit your query and let our AI agent do the heavy lifting
            </p>
          </div>
          <button
            onClick={onClose}
            style={{ background: "transparent", border: "none", fontSize: "24px", cursor: "pointer", color: "#64748b", padding: 0, width: "36px", height: "36px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "6px", transition: "all 0.2s ease" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#f1f5f9";
              e.currentTarget.style.color = "#0f172a";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "#64748b";
            }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))", gap: "20px", marginBottom: "24px" }}>
          {features.map((feature, index) => (
            <div
              key={index}
              style={{ padding: "20px", backgroundColor: "#f8fafc", borderRadius: "12px", border: "1px solid #e2e8f0", transition: "all 0.3s ease", cursor: "default", display: "flex", flexDirection: "column", gap: "12px" }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#ffffff";
                e.currentTarget.style.borderColor = feature.color;
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = "0 10px 25px -5px rgba(0,0,0,0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#f8fafc";
                e.currentTarget.style.borderColor = "#e2e8f0";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ width: "48px", height: "48px", borderRadius: "10px", backgroundColor: feature.color + "15", display: "flex", alignItems: "center", justifyContent: "center", color: feature.color, flexShrink: 0 }}>
                  {feature.icon}
                </div>
                <h4 style={{ fontSize: "18px", fontWeight: "600", color: "#0f172a", margin: 0, lineHeight: 1.2 }}>{feature.title}</h4>
              </div>
              <p style={{ fontSize: "14px", color: "#64748b", margin: 0, lineHeight: 1.6 }}>{feature.description}</p>
            </div>
          ))}
        </div>

        <div style={{ marginTop: "24px", padding: "20px", background: "linear-gradient(135deg, #0891b2 0%, #8b5cf6 100%)", borderRadius: "12px", textAlign: "center" }}>
          <p style={{ color: "white", fontSize: "18px", fontWeight: 600, margin: "0 0 12px 0" }}>
            Your personal AI research assistant that never stops working
          </p>
          <p style={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "14px", margin: 0 }}></p>
        </div>
      </div>
    </>
  );
}